import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, ChangeDetectionStrategy, inject } from "@angular/core";
import { CharTypeId } from "rl-common/rl-common.consts";
import { APKeyValueOption } from "rl-common/models/ap-key-value-option";
import { ICharacteristicData } from "rl-common/models/i-characteristic-data";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { IHierarchyLabelContext } from "rl-common/models/i-hierarchy-label-context";
import { RuleSource } from "rl-common/services/accounting-processes/models/rule-source";
import { RuleType } from "rl-common/services/accounting-processes/models/rule-type";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRule } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { SearchService } from "rl-common/services/search/search.service";
import { LovUtil } from "rl-common/utils/lov-util";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";

@Component({
	selector: "rl-account-group-rule",
	templateUrl: "./account-group-rule.component.html",
	styleUrls: ["./account-group-rule.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AsyncPipe]
})

export class AccountGroupRuleComponent implements OnChanges, OnDestroy {
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _searchService = inject(SearchService);

	@Input()
	accountGroup: IAccountingProcessAccountGroup;

	@Input()
	rule: IAccountingProcessAccountGroupRule;

	/**
	 * Two lists, not one merged list, because the two sources are unrelated field spaces that happen to
	 * overlap on value sets: an Amount rule on Revenue Type rendered BLANK (no contact field uses that
	 * set) while Amount rules on Media and Territory rendered the CONTACT field's label, since contact
	 * fields share those sets. resolvePicklistLabel() picks the list by rule.ruleSource, so each source
	 * is resolved only against its own fields -- the same split the build modal already makes.
	 */
	@Input()
	contactPicklistItems: ICharacteristicMetaData[] = [];

	@Input()
	amountPicklistItems: ICharacteristicMetaData[] = [];

	@Input()
	templateItems: APKeyValueOption[] = [];

	@Input()
	templateGroupItems: APKeyValueOption[] = [];

	/** shared across the whole Account Group -- see getHierarchyLabelContext() */
	@Input()
	hlcCache = new Map<string, IHierarchyLabelContext>();

	@Output()
	edit = new EventEmitter();

	@Output()
	delete = new EventEmitter();

	groupId: string;

	// Resolved in ngOnChanges rather than from getters. The getters these replaced assigned to
	// their backing field without returning it, so the first render was always blank and only
	// filled in on a later change detection pass -- invisible under Eager CD, but it would break
	// outright under OnPush.
	ruleTypeName: string;
	picklistLabel: string;
	picklistValueLabel: string;
	templateName: string;
	templateGroupName: string;

	/** the enum itself, so the template can compare against RuleType.* instead of magic numbers */
	readonly ruleTypes = RuleType;

	/** likewise, so the template can render Contact/Amount without a magic number */
	readonly ruleSources = RuleSource;

	private resolveRuleTypeName() {
		switch (this.rule.ruleType) {
			case RuleType.FieldValue:
				return "Field Value";
			case RuleType.Record:
				return "Record";
			case RuleType.Template:
				return "Template";
			case RuleType.TemplateGroup:
				return "Template Group";
			default:
				return "";
		}
	}

	/**
	 * Names the ONE field a rule targets when it carries a characteristic id, and otherwise names EVERY
	 * field on the rule's value set -- "Citizenship / Country / Country (H) / Organized In" rather than
	 * "Citizenship".
	 *
	 * That fallback is the legacy path, for rules saved before XCHR_ID existed on the table. Those
	 * persist a value SET, not a field, and a division routinely points several fields at one set (div 29
	 * has those four on the countries set, sharing a value source as well). So nothing in such a rule
	 * says which field was picked, and naming one of them implies a precision the rule does not have:
	 * CheckFieldValueRule still pools the values of every characteristic on the value set for them and
	 * requires all of them to be covered, so the rule really does read all four -- and a value in a
	 * sibling field BLOCKS it. Listing them is the honest rendering, and it makes the rules that need
	 * re-saving visible.
	 *
	 * Matched only within the rule's own source's fields, and on the value set rather than the value
	 * source -- the earlier `||` on charValueSourceID could resolve the wrong picklist when two
	 * characteristics share a source. Amount is always a single name in practice either way:
	 * amountRuleCmds dedupes by value set.
	 */
	private resolvePicklistLabel() {
		const picklistItems = this.rule.ruleSource === RuleSource.Amount ? this.amountPicklistItems : this.contactPicklistItems;
		const fields = this.rule.characteristicId
			? picklistItems?.filter(p => p.characteristicID === this.rule.characteristicId)
			: picklistItems?.filter(p => p.charValueSetID === this.rule.charValueSetId);
		if (!fields?.length) {
			return undefined;
		}

		// distinct: two characteristics on one value set can carry the same label, and "Country / Country"
		// reads as a rendering bug. Order comes from picklistItems, which is sorted by label upstream.
		return [...new Set(fields.map(f => f.label))].join(" / ");
	}

	private resolvePicklistValueLabel() {
		if (this.rule.ruleType !== RuleType.FieldValue || !this.rule.characteristicValueIds?.length) {
			return undefined;
		}

		const hlc = this.getHierarchyLabelContext();
		if (!hlc) {
			return undefined;
		}

		// getFriendlyLovLabels collapses a fully-selected branch back to its parent ("Europe" rather
		// than all forty children) and reports the gaps separately, so a rule on most-of-a-branch
		// stays readable. Same rendering as rl-dropdown-display-element.
		const charDatas = this.rule.characteristicValueIds.map(id => ({ valueID: id } as ICharacteristicData));
		const { included, excluded } = LovUtil.getFriendlyLovLabels(charDatas, hlc);
		if (!included.length) {
			return undefined;
		}

		return excluded.length
			? `${included[0]} <em>Excluding:</em> ${excluded.join(" | ")}`
			: included.join(" | ");
	}

	/**
	 * Building a HierarchyLabelContext walks the whole LOV, and different Rule Sets in a group
	 * routinely target the same field, so the cache is owned by the Account Group above and shared
	 * across every rule under it. Walking is also why listOfValues cannot be read flat: it holds
	 * only the ROOT nodes, and valueIdLabelMap is keyed by every node, branch and leaf alike.
	 */
	private getHierarchyLabelContext() {
		const cached = this.hlcCache.get(this.rule.charValueSourceId);
		if (cached) {
			return cached;
		}

		const listOfValues = this._oneConfig.getLovMetaData(this.rule.charValueSourceId)?.listOfValues;
		if (!listOfValues?.length) {
			return undefined;
		}

		const hlc = LovUtil.buildHierarchyLabelContext(listOfValues);
		this.hlcCache.set(this.rule.charValueSourceId, hlc);
		return hlc;
	}

	private resolveTemplateName() {
		return this.templateItems?.find(t => t.id === this.rule.templateId)?.value;
	}

	private resolveTemplateGroupName() {
		return this.templateGroupItems?.find(tg => tg.id === this.rule.templateGroupId)?.value;
	}

	_subs: Subscription[] = [];

	contactName$: Observable<string>;

	/** the record id contactName$ was built for; 0 when the rule carries no contact */
	private _contactRecordId = 0;

	/**
	 * On every input change, not just once: the rows are tracked by groupRuleId, so a reload after an
	 * edit hands the SAME component instance a new rule object -- ngOnInit would not run again and the
	 * row would keep rendering the old values. It also covers picklistItems/templateItems arriving
	 * after the rules do, which would otherwise leave the labels permanently blank.
	 */
	ngOnChanges(): void {
		if (!this.rule) {
			return;
		}

		this.groupId = this.accountGroup?.id;

		this.ruleTypeName = this.resolveRuleTypeName();
		this.picklistLabel = this.resolvePicklistLabel();
		this.picklistValueLabel = this.resolvePicklistValueLabel();
		this.templateName = this.resolveTemplateName();
		this.templateGroupName = this.resolveTemplateGroupName();

		// only a Record rule carries a contact; recordId is 0 otherwise, and asking for the title of
		// record 0 is a wasted round trip. Keyed on the id so the unrelated input changes that now
		// re-enter this method do not each fire another lookup for a contact already resolved.
		const recordId = this.rule.ruleType === RuleType.Record ? this.rule.recordId : 0;
		if (recordId !== this._contactRecordId) {
			this._contactRecordId = recordId;
			this.contactName$ = recordId
				? this._searchService.getTitles(CharTypeId.User, [recordId]).pipe(
					map(result => result.get(recordId))
				)
				: undefined;
		}
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

	editRule() {
		this.edit.emit();
	}

	deleteRule() {
		this.delete.emit();
	}
}
